import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Download, 
  Printer, 
  Share2, 
  Clock,
  AlertCircle,
  CheckSquare,
  Square,
  Users
} from 'lucide-react';
import { motion } from 'framer-motion';
import { sopData } from '../../data/sopData';
import BackButton from '../../../../components/BackButton';

const ChecklistDetail = () => {
  const { id } = useParams();
  const checklist = sopData.checklists.find(c => c.id === id);
  const [items, setItems] = useState(
    checklist?.items.map(item => ({ ...item, checked: false })) || []
  );

  if (!checklist) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Checklist Not Found</h2>
        <p className="text-gray-500 mt-2">The checklist you're looking for doesn't exist.</p>
        <BackButton label="Back to Checklists" />
      </div>
    );
  }

  const toggleItem = (index) => {
    const newItems = [...items];
    newItems[index].checked = !newItems[index].checked;
    setItems(newItems);
  };

  const progress = Math.round(
    (items.filter(item => item.checked).length / items.length) * 100
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto"
    >
      <div className="mb-8">
        <BackButton label="Back to Checklists" />
        
        <div className="flex justify-between items-start mt-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{checklist.title}</h1>
            <p className="text-gray-500 mt-2">
              {checklist.category} • {checklist.frequency}
            </p>
          </div>
          <div className="flex gap-3">
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Share2 className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Printer className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-sm font-medium text-gray-700">Progress</h2>
          <span className="text-sm font-medium text-gray-700">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Checklist Items */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="space-y-4">
          {items.map((item, index) => (
            <div 
              key={index}
              className="flex items-start gap-4 p-4 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <button
                onClick={() => toggleItem(index)}
                className="mt-1"
              >
                {item.checked ? (
                  <CheckSquare className="w-5 h-5 text-green-600" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <div className="flex-1">
                <h3 className={`font-medium ${item.checked ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                  {item.task}
                </h3>
                {item.description && (
                  <p className={`text-sm mt-1 ${item.checked ? 'text-gray-400' : 'text-gray-500'}`}>
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Information */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Assigned To</h2>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gray-400" />
            <span className="text-gray-600">{checklist.assignedTo}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Schedule</h2>
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-gray-400" />
            <span className="text-gray-600">{checklist.schedule}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ChecklistDetail; 